import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Chip, CircularProgress, Alert
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const CompaniesPage = () => {
  const { currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, company: null });

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch companies');

      const data = await response.json();
      setCompanies(data.companies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSave = async (company) => {
    try {
      const token = await currentUser.getIdToken();
      // Distinguish create vs edit: editing means the company already exists in the list
      const isEdit = companies.some(c => c.id === company.id);
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit
        ? `${API_BASE_URL}/api/v1/admin/companies/${company.id}`
        : `${API_BASE_URL}/api/v1/admin/companies`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(company)
      });

      if (!response.ok) throw new Error('Failed to save company');

      setEditDialog({ open: false, company: null });
      fetchCompanies();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <AdminLayout title="Companies"><CircularProgress /></AdminLayout>;

  return (
    <AdminLayout title="Companies" requireSuperAdmin>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditDialog({ open: true, company: { name: '', billing_email: '' } })}
        >
          Add Company
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Billing Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.id}>
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.id}</TableCell>
                <TableCell>{company.billing_email}</TableCell>
                <TableCell>
                  {company.is_platform_owner && <Chip label="Platform Owner" color="primary" size="small" />}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => setEditDialog({ open: true, company })}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CompanyDialog
        open={editDialog.open}
        company={editDialog.company}
        onClose={() => setEditDialog({ open: false, company: null })}
        onSave={handleSave}
      />
    </AdminLayout>
  );
};

const CompanyDialog = ({ open, company, onClose, onSave }) => {
  const [form, setForm] = useState(company || {});

  useEffect(() => {
    setForm(company || {});
  }, [company]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{company?.id ? 'Edit Company' : 'Add Company'}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Company ID"
          value={form.id || ''}
          onChange={(e) => setForm({ ...form, id: e.target.value })}
          disabled={!!company?.id}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Name"
          value={form.name || ''}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Billing Email"
          type="email"
          value={form.billing_email || ''}
          onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(form)} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompaniesPage;
